import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/common";
import { Save, RotateCcw, ChevronDown } from "lucide-react";

const CAMPI = [["chiamate_giorno", "Chiamate al giorno", "chiamate"], ["notizie_settimana", "Notizie a settimana", "notizie"],
  ["incarichi_settimana", "Incarichi a settimana", "incarichi"], ["vendite_settimana", "Vendite a settimana", "vendite"]];
const inp = "w-16 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm mono";

function Riga({ p, info, onSaved }) {
  const mul = (k) => (k === "chiamate_giorno" ? info.giorni_lavorativi : info.settimane);
  const [v, setV] = useState(p);
  const [m, setM] = useState(Object.fromEntries(CAMPI.map(([, , mk]) => [mk, p.mese[mk].manuale ? p.mese[mk].valore : null])));
  const [busy, setBusy] = useState(false);
  const autoDi = (k) => (Number(v[k]) || 0) * mul(k);
  const cambiato = CAMPI.some(([k, , mk]) => Number(v[k]) !== Number(p[k]) || m[mk] !== (p.mese[mk].manuale ? p.mese[mk].valore : null));
  const salva = async () => {
    setBusy(true);
    try {
      await api.put(`/obiettivi-team/${p.agent_id}`, { ...Object.fromEntries(CAMPI.map(([k]) => [k, Number(v[k]) || 0])), mese: m });
      toast.success(`Obiettivi di ${p.nome} salvati`); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const fatti = p.fatti_mese || {};
  return (
    <div className="card-immo p-4" data-testid={`obt-riga-${p.agent_id}`}>
      <div className="flex items-start gap-3 mb-3">
        <span className="w-10 h-10 rounded-full bg-[#2B2B2B] text-white text-xs font-bold flex items-center justify-center shrink-0">{(p.nome || "?").split(/\s+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase()}</span>
        <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{p.nome}</div><div className="text-[11px] text-[var(--muted)]">{p.ruolo === "buyer" ? "Buyer" : "Agente"}{p.team ? ` · ${p.team}` : ""}</div></div>
      </div>
      <div className="space-y-3">{CAMPI.map(([k, l, mk]) => {
        const ob = m[mk] ?? autoDi(k), f = fatti[mk] || 0, pct = ob ? Math.min(100, Math.round((f / ob) * 100)) : 0;
        return (
          <div key={k}>
            <div className="flex items-center justify-between text-xs mb-1"><span className="font-semibold capitalize">{mk}</span>
              <span data-testid={`obt-progresso-${mk}-${p.agent_id}`} className="mono"><b>{f}</b> / {ob} nel mese</span></div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: pct >= 100 ? "#22C55E" : "#FC82ED" }} /></div>
            <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--muted)] flex-wrap">
              <span>{l}</span>
              <input type="number" min="0" data-testid={`obt-${k}-${p.agent_id}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={inp} />
              <span>Mese</span>
              <input type="number" min="0" data-testid={`obt-mese-${mk}-${p.agent_id}`} value={m[mk] ?? autoDi(k)} onChange={(e) => setM({ ...m, [mk]: e.target.value === "" ? null : Number(e.target.value) })}
                className={`w-16 px-1.5 py-1 rounded-md border text-xs mono ${m[mk] !== null ? "border-amber-400 bg-amber-50" : "border-[var(--border)]"}`} />
              {m[mk] !== null ? <button type="button" title="Torna al calcolo automatico" data-testid={`obt-mese-auto-${mk}-${p.agent_id}`} onClick={() => setM({ ...m, [mk]: null })}><RotateCcw size={11} /></button> : <span>auto</span>}
            </div>
          </div>);
      })}</div>
      <button data-testid={`obt-salva-${p.agent_id}`} disabled={!cambiato || busy} onClick={salva}
        className="mt-4 w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-[#111111] text-white text-xs font-semibold disabled:opacity-30"><Save size={13} /> Salva</button>
    </div>
  );
}

const gruppi = (persone, teams) => {
  const m = new Map((teams || []).map((t) => [t.team_id || "", { t, g: [] }]));
  persone.forEach((p) => { const k = p.team_id || ""; if (!m.has(k)) m.set(k, { t: { team_id: p.team_id, team: p.team, direttore: p.direttore }, g: [] }); m.get(k).g.push(p); });
  return [...m.entries()].sort((a, b) => (!a[0] - !b[0]) || (a[1].t.team || "").localeCompare(b[1].t.team || ""));
};

function CardTeam({ tid, t, g, info, aperta, onToggle, onSaved }) {
  const pcts = CAMPI.map(([, , mk]) => {
    const ob = g.reduce((s, p) => s + (p.mese?.[mk]?.valore || 0), 0), f = g.reduce((s, p) => s + ((p.fatti_mese || {})[mk] || 0), 0);
    return [mk, f, ob, ob ? Math.min(100, Math.round((f / ob) * 100)) : 0];
  });
  const tot = Math.round(pcts.reduce((s, x) => s + x[3], 0) / pcts.length);
  const id = tid || "senza";
  return (
    <div className={`card-immo ${aperta ? "sm:col-span-2 xl:col-span-3" : ""}`} data-testid={`obt-team-${id}`}>
      <button type="button" data-testid={`obt-team-toggle-${id}`} onClick={onToggle} className="w-full text-left p-5 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base">{t.team || "Senza team"}</div>
          <div className="text-xs text-[var(--muted)]">Direttore: {t.direttore || "—"} · <span data-testid={`obt-team-persone-${id}`}>{g.length} {g.length === 1 ? "persona" : "persone"}</span></div>
          <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${tot}%`, background: tot >= 100 ? "#22C55E" : "#FC82ED" }} /></div>
          <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-[var(--muted)]">{pcts.map(([mk, f, ob]) => <span key={mk} className="capitalize">{mk} <b className="mono text-[#2B2B2B]">{f}/{ob}</b></span>)}</div>
        </div>
        <span data-testid={`obt-team-pct-${id}`} className="mono text-2xl font-extrabold">{tot}%</span>
        <ChevronDown size={18} className={`transition-transform ${aperta ? "rotate-180" : ""}`} />
      </button>
      {aperta && (g.length ? <div className="px-5 pb-5 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{g.map((p) => <Riga key={`${p.agent_id}-${JSON.stringify(p.mese)}-${CAMPI.map(([k]) => p[k]).join(",")}`} p={p} info={info} onSaved={onSaved} />)}</div>
        : <p className="px-5 pb-5 text-sm text-[var(--muted)]">Nessun agente o buyer in questo team.</p>)}
    </div>
  );
}

export default function ObiettiviTeam() {
  const [aperti, setAperti] = useState({});
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get("/obiettivi-team").then((r) => setD(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="fade-up pb-20" data-testid="obiettivi-team-page">
      <SectionTitle sub="Obiettivi di attività per ogni agente e buyer. Li vedono nella loro Home ma non possono modificarli.">Obiettivi team</SectionTitle>
      {err ? <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">{err}</div> : !d ? <div className="skeleton h-48 rounded-2xl" /> : (
        <div>
          <p data-testid="obt-vista" className="text-sm font-semibold mb-2">{d.vista?.tipo === "team" ? `Stai vedendo il tuo team: ${d.vista.team_name}` : "Stai vedendo tutti i team"}</p>
          <p className="text-xs text-[var(--muted)] mb-4" data-testid="obt-info-mese">Obiettivo del mese ({d.mese.split("-").reverse().join("/")}) calcolato da solo: chiamate × {d.giorni_lavorativi} giorni lavorativi, notizie/incarichi/vendite × {d.settimane} settimane. Puoi modificarlo a mano (in giallo); <RotateCcw size={10} className="inline" /> torna al calcolo automatico.</p>
          {!(d.teams || []).length && d.persone.length === 0 ? <p className="text-sm text-[var(--muted)]">Nessun agente o buyer nel team.</p> : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 items-start">{gruppi(d.persone, d.teams).map(([tid, { t, g }]) => {
              const k = tid || "senza", aperta = aperti[k] ?? d.vista?.tipo === "team";
              return <CardTeam key={k} tid={tid} t={t} g={g} info={d} aperta={aperta} onToggle={() => setAperti({ ...aperti, [k]: !aperta })} onSaved={load} />;
            })}</div>
          )}
        </div>
      )}
    </div>
  );
}
